export interface BalanceBookingRecord {
  id: string;
  bookingDate: string;
  sourceModule: string;
  sourceType: string;
  sourceId: string;
  sourceLineId: string;
}

export interface BalancePostingHistoryItem {
  id: string;
  postingDate: string;
  balanceType: string;
  label: string;
  unit: string;
  amountHours: number;
  amountDays: number;
  ruleKey: string;
  bookingRecord: BalanceBookingRecord;
  metadata: Record<string, unknown>;
}

export interface BalancePostingHistoryPage {
  items: BalancePostingHistoryItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface BalanceOverviewEntry {
  typeKey: string;
  label: string;
  icon: string;
  color: string;
  unit: string;
  /** Balance including EVERY posting — future-dated plan postings included. */
  currentValue: number;
  /** Balance up to and including the server today ("Ist bis heute", spec §3d-18). */
  todayValue: number;
  budgetValue: number | null;
  zone: 'green' | 'amber' | 'red' | null;
}

export interface BalanceOverviewRow {
  personId: string;
  firstName: string;
  lastName: string;
  /**
   * `YYYY-MM-DD` of the person's most recent balance posting up to and
   * including the server today — future plan postings never surface as
   * "Letzte Buchung" (spec §3d-18). null = never posted.
   */
  lastPostingDate: string | null;
  /**
   * Net minutes of the person's future plan entries in the CURRENT month
   * (bookings after the server today, journal-relevant sources only) — the
   * "Geplant" column of the overview (spec §3d-18, V3). Future plan bookings
   * carry no balance postings before confirmation, so this sum comes from the
   * booking ledger, mirroring the person journal's "Geplant" total.
   */
  plannedMinutes: number;
  balances: BalanceOverviewEntry[];
}

export interface EnrichedBalance {
  id: string | null;
  balanceType: string;
  currentBalanceHours: number;
  todayBalanceHours: number;
  unit: string;
  /** Denomination of `todayBalanceHours`; always equals `unit` (day-based balances convert through the reference day). `currentBalanceHours` stays hours-denominated. */
  valueUnit: string;
  label: string;
  translations: Record<string, { label?: string }> | null;
  icon: string;
  color: string;
  defaultAnnualBudget: number | null;
  /** Admin flag: surface this balance as a saldo widget in "Deine Woche". */
  showInWeek: boolean;
  /** Hours locked by pending absence approval requests of this balance type. */
  pendingHours: number;
  /** Days locked by pending absence approval requests of this balance type. */
  pendingDays: number;
  /** `currentBalance − pending` in the type's unit — never recompute this at the caller. */
  availableBalance: number;
}
